import { useState } from 'react'
import { Plus } from 'lucide-react'
import { useCategories, useDeleteCategory } from '@/hooks/useCategories'
import { useTablePagination } from '@/hooks/useTablePagination'
import type { Category } from '@/lib/supabase'
import { Button, PageHeader } from '@atrvd/saas-ui'
import { ConfirmationDialog } from '@/components/admin/ConfirmationDialog'
import { CategoriesTable } from '@/components/banking/CategoriesTable'
import { tableQueryState } from '@/components/common/queryErrorDisplay'
import { CategoryFormDialog } from '@/components/banking/CategoryFormDialog'

/**
 * Settings page for managing transaction categories
 * Allows users to create, edit, and delete categories for their organization
 */
export function SettingsCategoriesPage() {
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [deletingCategory, setDeletingCategory] = useState<Category | null>(null)
  // The total comes back with the page (`{ items, total }`), so it goes
  // through `forTotal` after the page query.
  const pagination = useTablePagination({ totalItems: undefined })

  // Type filter removed — categories no longer have a user-facing type concept
  const categoriesQuery = useCategories(pagination.page, pagination.pageSize)
  const { data: categoriesData } = categoriesQuery
  const deleteCategory = useDeleteCategory()

  // Extract categories array and totals from paginated response
  const categories = categoriesData?.items ?? []
  const totalCategories = categoriesData?.total ?? 0

  // A page past the end (delete the only row of the last page) returns an
  // empty range while `total` stays correct — `forTotal` moves to the new last
  // page instead of showing "No categories yet" with the footer gone.
  const { dataTablePagination } = pagination.forTotal({
    total: categoriesData?.total,
    isFetching: categoriesQuery.isFetching,
  })

  /**
   * Handle creating a new category
   */
  const handleCreate = () => {
    setEditingCategory(null)
    setIsFormDialogOpen(true)
  }

  /**
   * Handle editing an existing category
   */
  const handleEdit = (category: Category) => {
    setEditingCategory(category)
    setIsFormDialogOpen(true)
  }

  /**
   * Handle opening delete confirmation dialog
   */
  const handleDeleteClick = (category: Category) => {
    setDeletingCategory(category)
  }

  /**
   * Handle confirming category deletion
   */
  const handleDeleteConfirm = async () => {
    if (!deletingCategory) return

    try {
      await deleteCategory.mutateAsync(deletingCategory.id)
      setDeletingCategory(null)
    } catch (error) {
      // Error is handled by the mutation's onError callback
      // Close dialog on error too
      setDeletingCategory(null)
    }
  }

  /**
   * Handle closing the form dialog
   */
  const handleFormDialogClose = () => {
    setIsFormDialogOpen(false)
    setEditingCategory(null)
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Categories ${totalCategories > 0 ? `(${totalCategories})` : ''}`}
        description="Manage transaction categories for your organization"
        actions={
          <Button
            onClick={handleCreate}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Category
          </Button>
        }
      />

      {/* No Card and no section heading: DataTable draws its own frame, and the
          page header already names the list and its count. */}
      <CategoriesTable
        categories={categories}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
        // No footer up to 20 categories (lib/pagination.ts).
        pagination={dataTablePagination}
        {...tableQueryState(categoriesQuery, categories.length)}
      />

      {/* Category Form Dialog */}
      <CategoryFormDialog
        open={isFormDialogOpen}
        onOpenChange={handleFormDialogClose}
        category={editingCategory}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={!!deletingCategory}
        onOpenChange={(open: boolean) => !open && !deleteCategory.isPending && setDeletingCategory(null)}
        title="Delete Category"
        description={`Are you sure you want to delete "${deletingCategory?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        onConfirm={handleDeleteConfirm}
        isLoading={deleteCategory.isPending}
        variant="destructive"
      />
    </div>
  )
}
